import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Container, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import DefaultCard from '../../../components/DefaultCard';
import IconButton from '../../../components/IconButton/IconButton';
import Link from '../../../components/Link';
import ModalHeader from '../../../components/ModalHeader';
import { RadioGroup } from '../../../components/Radio';
import SelectableCard from '../../../components/SelectableCard';
import { EnrollmentStore } from '../../../providers/enrollment';
import { getAllSpousalSurcharges } from '../../../services/enrollment';

export type customSpousalSurchargeModalProps = {
  showSpousalSurchargeModal: boolean;
  handleCloseSpousalSurchargeModal: () => void;
};

const SpousalSurchargeModal = ({
  showSpousalSurchargeModal,
  handleCloseSpousalSurchargeModal,
}: customSpousalSurchargeModalProps) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [spousalSurchargeData, setSpousalSurchargeData] = React.useState<any>(
    {}
  );

  const { activeHealthBenefits, updateCost, updatePlanName } =
    useContext(EnrollmentStore);

  const [radioButtonValue, setRadioButtonValue] = React.useState<
    string | number
  >(9);

  const [tellMeMore, setTellMeMore] = useState(false);

  useEffect(() => {
    const data = getAllSpousalSurcharges();

    if (data) {
      setSpousalSurchargeData(data);
    }
  }, [spousalSurchargeData]);

  useEffect(() => {
    const currentBenefit = activeHealthBenefits.find(
      (benefit: any) => benefit.type === 'spousal-surcharge'
    );
    const index = spousalSurchargeData.plans?.findIndex(
      (plan: any) => plan.id === currentBenefit?.plan?.id
    );
    setRadioButtonValue(index);
  }, [activeHealthBenefits, spousalSurchargeData]);

  const handleRadioButtonValue = (e: string | number) => {
    setRadioButtonValue(e);
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleTellMeMore = () => {
    setTellMeMore((prev) => !prev);
  };

  return (
    <>
      {spousalSurchargeData ? (
        <Modal
          show={showSpousalSurchargeModal}
          onHide={handleCloseSpousalSurchargeModal}
          dialogClassName="modal-xl"
          className="custom-health-savings-modal"
        >
          <ModalHeader
            leftIcon={light('family')}
            leftIconVariant="icon-emphasized"
            rightButton={
              <div onClick={toggleDetails} className={'icon-button-wrapper'}>
                <IconButton
                  variant="icon-default"
                  icon={light('circle-exclamation')}
                  extLabel="Benefit Info"
                />
              </div>
            }
          >
            <h4 className="text-sm-20-lg-32">
              {spousalSurchargeData['generic']
                ? spousalSurchargeData['generic']['title']
                : ''}
            </h4>
          </ModalHeader>
          <ModalBody>
            <Row className={`expandable-wrapper ${showDetails ? 'open' : ''}`}>
              <div className="d-flex justify-content-center">
                <DefaultCard
                  closeButton={
                    <Link 
                      className='pt-0' 
                      isAlignAtEnd 
                      isWhiteIcon 
                      rightIcon={light('close')}
                      onClick={toggleDetails}
                    />                      
                  }
                  variant="blue-motif"
                  leftHeading={
                    spousalSurchargeData['generic']
                      ? spousalSurchargeData['generic']['info']['title']
                      : ''
                  }
                  leftSubheading={
                    <Container className={'px-0 d-flex flex-column'}>
                      {spousalSurchargeData['generic'] ? (
                        <>
                          <span>
                            {
                              spousalSurchargeData['generic']['info'][
                                'description'
                              ]
                            }
                          </span>
                          {tellMeMore && (
                            <span className={'mt-1'}>
                              {
                                spousalSurchargeData['generic']['info'][
                                  'additionalDescription'
                                ]
                              }
                            </span>
                          )}
                        </>
                      ) : (
                        ''
                      )}
                    </Container>
                  }
                  button={
                    <Button
                      onClick={handleTellMeMore}
                      variant="primary-shadow"
                      size="sm"
                    >
                      {tellMeMore ? 'Tell me less' : 'Tell me more'}
                    </Button>
                  }
                  className={'mb-12'}
                />
              </div>
            </Row>
            <Container className="p-0">
              <Row className="mb-7 mb-lg-12">
                <h5 className="text-sm-14-lg-24">
                  What option would you like?
                </h5>
              </Row>
              <Row>
                <RadioGroup
                  name={'pricing-test1'}
                  value={radioButtonValue}
                  onChange={handleRadioButtonValue}
                >
                  <div>
                    {spousalSurchargeData['plans']
                      ? spousalSurchargeData['plans'].map(
                          (
                            plan: {
                              title: string;
                              cost: any;
                              coverage: number;
                            },
                            i: any
                          ) => (
                            <SelectableCard
                              title={plan.title}
                              prePaydPeriod={plan.cost}
                              radioButtonValue={i}
                              selected={radioButtonValue === i}
                              disabled={
                                i === spousalSurchargeData['plans'].length - 1
                              }
                              key={i}
                            />
                          )
                        )
                      : ''}
                  </div>
                </RadioGroup>
              </Row>
            </Container>
          </ModalBody>
          <Modal.Footer className="pt-7">
            <Button
              onMouseDown={handleCloseSpousalSurchargeModal}
              className="text-nowrap mt-7 mt-lg-0"
              variant="primary-outline"
            >
              Cancel
            </Button>
            <Button
              onMouseDown={() => {
                updateCost(
                  'spousal-surcharge',
                  spousalSurchargeData.plans[radioButtonValue]
                );
                updatePlanName(
                  'spousal-surcharge',
                  spousalSurchargeData.plans[radioButtonValue]
                );
                handleCloseSpousalSurchargeModal();
              }}
              className="text-nowrap"
              variant="primary"
            >
              Continue
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        'NO DATA'
      )}
    </>
  );
};

export default SpousalSurchargeModal;
